import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, switchMap, withLatestFrom} from 'rxjs/operators';
import {combineLatest, debounceTime, map, of, tap} from 'rxjs';
import {ProposalService} from '../../../services/proposal-service/proposal.service';
import {
  ProposalDetailPageActions, ProposalNumberActions,
  ProposalOverview,
  ProposalSelectedActions,
  ProposalTileActions,
  ProposalTransmissionActions
} from '../actions/proposal.actions';
import {OfferService} from '../../../services/offer/offer.service';
import {Store} from '@ngrx/store';
import {ProposalAppState} from '../reducers/proposal.reducer';
import {selectCurrentFilter} from '../selectors/proposal.selectors';
import {Offer} from '../../../model/offer/offer';
import {TransmissionService} from '../../../services/transmission-service/transmission.service';
import {TranslateService} from '@ngx-translate/core';
import {UserService} from '../../../services/user/user.service';
import {MatSnackBar} from '@angular/material/snack-bar';

@Injectable()
export class ProposalEffects {

  loadProposalTiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalOverview.updateFilter, ProposalTileActions.load),
      withLatestFrom(this.proposalStore$.select(selectCurrentFilter)),
      debounceTime(100),
      switchMap(([newFilter, filterQuery]) => {
        return this.proposalService.filteredProposalTiles(filterQuery).pipe(
          map((proposalTiles) => ProposalTileActions.loaded({proposalTiles: proposalTiles})),
          catchError((error) => of(ProposalTileActions.errorLoading({error}))
          )
        );
      })
    )
  );

  deselectOnFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalOverview.updateFilter),
      map(() => ProposalSelectedActions.deselect())
    ));

  loadInsuranceContractSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalSelectedActions.load),
      switchMap((({proposalId}) =>
            this.proposalService.getProposal(proposalId).pipe(
              map((proposalSelected) => ProposalSelectedActions.loaded({proposalSelected: proposalSelected})),
              catchError((error) => of(ProposalSelectedActions.errorLoading(error))))
        )
      )
    )
  );

  loadUserAllowedToView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalSelectedActions.loaded),
      switchMap(({proposalSelected}) => {
          if (!proposalSelected.proposalInformation.privateProposal || proposalSelected.proposalInformation.usersAllowedToView == null || proposalSelected.proposalInformation.usersAllowedToView.length == 0) {
            return of(ProposalSelectedActions.loadedUserAllowedToView({usersAllowedToView: []}));
          }
          return combineLatest(proposalSelected
            .proposalInformation
            .usersAllowedToView
            .map(id => this.userService.getUserById(id))).pipe(
            map((usersAllowedToView) => ProposalSelectedActions.loadedUserAllowedToView({usersAllowedToView})),
            catchError((error) => of(ProposalSelectedActions.errorLoading(error)))
          );
        }
      )
    )
  );

  showRetractRejection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalSelectedActions.loaded),
      switchMap(({proposalSelected}) =>
        this.offerService.getOffersByAd(proposalSelected.proposalInformation.id).pipe(
          map((offers) => ProposalSelectedActions.loadedOffers({proposalSelectedOffers: offers})),
          catchError((error) => of(ProposalSelectedActions.errorLoading(error)))
        )
      )
    )
  );

  detailPageLoadInsuranceContractTile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalDetailPageActions.load),
      switchMap(({proposalId}) =>
        this.proposalService.getProposalTile(proposalId).pipe(
          map((proposalTile) => ProposalDetailPageActions.loadedProposalTile({proposalTile})),
          catchError((error) => of(ProposalDetailPageActions.errorLoading(error))),
        )
      )
    )
  );
  detailPageLoadProposal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalDetailPageActions.load),
      switchMap(({proposalId}) =>
        this.proposalService.getProposal(proposalId).pipe(
          map((proposal) => ProposalDetailPageActions.loadedProposal({proposal})),
          catchError((error) => of(ProposalDetailPageActions.errorLoading(error))),
        )
      )
    )
  );

  loadProposalTransmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalTransmissionActions.load),
      switchMap(({id, secret}) =>
        this.transmissionService.loadProposalTransmission(id, secret).pipe(
          map((proposal) => ProposalTransmissionActions.loaded({proposal})),
          catchError((error) => of(ProposalTransmissionActions.errorLoading(error))),
        )
      )
    )
  );


  detailPageLoadOffers = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalDetailPageActions.load),
      switchMap(({proposalId}) =>
        this.offerService.getOffersByAd(proposalId).pipe(
          map((offers) => ProposalDetailPageActions.loadedOffers({offers})),
          catchError((error) => of(ProposalDetailPageActions.errorLoading(error))),
        )
      )
    )
  );
  acceptOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalDetailPageActions.acceptOffer),
      switchMap(({offerId, includeCompanyName, includeCoverage, includeClausesAndConditions, includePremium, proposalId}) =>
        this.offerService.acceptOffer({
          offerId,
          includeCompanyName,
          includeCoverage,
          includeClausesAndConditions,
          includePremium,
          proposalId
        }).pipe(
          tap({
            next: () => this.snackbar.open(this.translate.instant('offer.offerAccepted'), '', {
              panelClass: ['success-snackbar', 'custom-snackbar'],
            })
          }),
          map((offer) => ProposalDetailPageActions.load({proposalId: offer.proposalId})),
        ))
    ));
  retractOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalDetailPageActions.retractOffer),
      switchMap(({offerId, proposalId}) =>
        this.offerService.retractOffer(offerId, proposalId).pipe(
          tap({
            next: () => this.snackbar.open(this.translate.instant('offer.offerRetracted'), '', {
              panelClass: ['success-snackbar', 'custom-snackbar'],
            })
          }),
          map((offer) => ProposalDetailPageActions.load({proposalId: offer.proposalId})),
        ))
    ));

  startRejectionLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalTileActions.reject),
      map(() => ProposalTileActions.startRejectionLoading())
    )
  );

  stopRejectionLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalTileActions.rejected, ProposalTileActions.errorRejecting),
      map(() => ProposalTileActions.stopRejectionLoading())
    )
  );

  rejectInsuranceContract = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalTileActions.reject),
      switchMap(({rejectionId, rejectionOffer}) =>
        this.proposalService.rejectInsuranceContract(rejectionId, rejectionOffer).pipe(
          map((offer: Offer) => {
            this.snackbar.open(this.translate.instant('proposalRejected'), '', {
              panelClass: ['success-snackbar', 'custom-snackbar']
            });
            return ProposalTileActions.rejected({rejectionOffer});
          }),
          catchError((error) => of(ProposalTileActions.errorRejecting(error)))
        )
      )
    )
  );
  finishedRejection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalTileActions.rejected),
      map(() => ProposalOverview.resetPagination())
    )
  );

  startRetractRejectionLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalTileActions.retractRejection),
      map(() => ProposalTileActions.startRetractRejectionLoading())
    )
  );

  stopRetractRejectionLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalTileActions.retractedRejection, ProposalTileActions.errorRejecting),
      map(() => ProposalTileActions.stopRetractRejectionLoading())
    )
  );

  retractRejection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalTileActions.retractRejection),
      switchMap(({rejectionId, proposalId}) =>
        this.proposalService.retractRejections(rejectionId, proposalId).pipe(
          map((offer: Offer) => {
            this.snackbar.open(this.translate.instant('retractRejection'), '', {
              panelClass: ['success-snackbar', 'custom-snackbar']
            });
            return ProposalTileActions.retractedRejection();
          }),
          catchError((error) => of(ProposalTileActions.errorRejecting(error)))
        )
      )
    )
  );
  finishedRetractingRejection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalTileActions.retractedRejection),
      map(() => ProposalOverview.resetPagination())
    )
  );

  resetPagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalOverview.resetPagination),
      map(() => ProposalTileActions.load())
    )
  );

  loadProposalNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalNumberActions.loadProposalNumber, ProposalTileActions.load, ProposalOverview.updateFilter),
      withLatestFrom(this.proposalStore$.select(selectCurrentFilter)),
      debounceTime(100),
      switchMap(([newFilter, filterQuery]) => {
          return this.proposalService.getProposalNumber(filterQuery).pipe(
            map((proposalNumber) => ProposalNumberActions.proposalNumberLoaded({proposalNumber})),
            catchError((error) => of(ProposalNumberActions.proposalNumberError({error})))
          )
        }
      )
    )
  );

  loadUserAllowedToViewForPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalDetailPageActions.proposalPreview),
      switchMap(({proposalPreview}) => {
          if (!proposalPreview.proposalInformation.privateProposal || proposalPreview.proposalInformation.usersAllowedToView == null || proposalPreview.proposalInformation.usersAllowedToView.length == 0) {
            return of(ProposalSelectedActions.loadedUserAllowedToView({usersAllowedToView: []}));
          }
          return combineLatest(proposalPreview
            .proposalInformation
            .usersAllowedToView
            .map(id => this.userService.getUserById(id))).pipe(
            map((usersAllowedToView) => ProposalSelectedActions.loadedUserAllowedToView({usersAllowedToView})),
            catchError((error) => of(ProposalSelectedActions.errorLoading(error)))
          );
        }
      )
    )
  );

  loadPrivateNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalDetailPageActions.loadPrivateNotes),
      switchMap(({userId, proposalId}) =>
        this.offerService.getPrivateNoteByUserIdAndProposalId(userId, proposalId).pipe(
          map((privateNotes) => ProposalDetailPageActions.loadedPrivateNotes({privateNotes: privateNotes}))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly proposalService: ProposalService,
    private readonly offerService: OfferService,
    private readonly proposalStore$: Store<ProposalAppState>,
    private readonly transmissionService: TransmissionService,
    private readonly snackbar: MatSnackBar,
    private readonly translate: TranslateService,
    private readonly userService: UserService
  ) {
  }
}
